import React, { useState } from 'react';
import axios from 'axios';
import { services, testimonials, bookings, contactSubmissions } from '../mock';
import { siteConfig } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Building2, 
  Home as HomeIcon, 
  Trash2, 
  Sparkles, 
  HardHat, 
  Calendar as CalendarIcon,
  Phone,
  Mail,
  MapPin,
  Star,
  CheckCircle2,
  MessageSquare,
  Clock,
  Award,
  Users,
  Leaf
} from 'lucide-react';
import { format } from 'date-fns';

const iconMap = {
  Building2,
  Home: HomeIcon,
  Trash2,
  Sparkles,
  HardHat,
  Calendar: CalendarIcon
};

const Home = () => {
  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  
  // State for confirmation dialogs
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [showContactSuccess, setShowContactSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState({ name: '', service: '', phone: '' });
  
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: undefined,
    message: ''
  });
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading state
    toast({
      title: "Sending your booking request...",
      description: "Please wait a moment.",
    });
    
    try {
      // Save to database via backend
      const backendResponse = await axios.post(`${API}/booking`, {
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        service: bookingForm.service,
        date: bookingForm.date ? format(bookingForm.date, 'PPP') : '',
        message: bookingForm.message
      });
      
      // Send email notification via Web3Forms (client-side)
      const emailData = new FormData();
      emailData.append('access_key', process.env.REACT_APP_WEB3FORMS_ACCESS_KEY);
      emailData.append('subject', `New Booking Request - ${bookingForm.service}`);
      emailData.append('from_name', 'DSL Jamaica Website');
      emailData.append('email', 'info@dsljm.com');
      emailData.append('message', `
New Booking Request Received:

Customer Name: ${bookingForm.name}
Email: ${bookingForm.email}
Phone: ${bookingForm.phone}
Service: ${bookingForm.service}
Preferred Date: ${bookingForm.date ? format(bookingForm.date, 'PPP') : 'Not specified'}
Additional Notes: ${bookingForm.message}

Please contact the customer to confirm the booking.
      `);
      
      // Send email (don't wait for response)
      fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: emailData
      }).catch(() => {
        // Silently fail if email doesn't send
      });
      
      if (backendResponse.data.success) {
        // Store submitted data for confirmation dialog
        setSubmittedData({
          name: bookingForm.name,
          service: bookingForm.service,
          phone: bookingForm.phone
        });
        
        // Show success dialog
        setShowBookingSuccess(true);
        
        // Reset form
        setBookingForm({ name: '', email: '', phone: '', service: '', date: undefined, message: '' });
      }
    } catch (error) {
      toast({
        title: "❌ Failed to Submit Booking",
        description: `Sorry, there was an error. Please call us directly at 876-799-2106 or WhatsApp +8767992106, or try again in a few moments.`,
        variant: "destructive",
        duration: 8000,
      });
      console.error('Booking error:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading state
    toast({
      title: "Sending your message...",
      description: "Please wait a moment.",
    });
    
    try {
      // Save to database via backend
      const backendResponse = await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message
      });
      
      // Send email notification via Web3Forms (client-side)
      const emailData = new FormData();
      emailData.append('access_key', process.env.REACT_APP_WEB3FORMS_ACCESS_KEY);
      emailData.append('subject', `New Contact Message from ${contactForm.name}`);
      emailData.append('from_name', 'DSL Jamaica Website');
      emailData.append('email', 'info@dsljm.com');
      emailData.append('message', `
New Contact Form Submission:

Name: ${contactForm.name}
Email: ${contactForm.email}
Message: ${contactForm.message}

Please respond to the customer's inquiry.
      `);
      
      // Send email (don't wait for response)
      fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: emailData
      }).catch(() => {
        // Silently fail if email doesn't send
      });
      
      if (backendResponse.data.success) {
        // Store submitted data for confirmation dialog
        setSubmittedData({
          name: contactForm.name,
          service: 'contact inquiry',
          phone: ''
        });
        
        // Show success dialog
        setShowContactSuccess(true);
        
        setContactForm({ name: '', email: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "❌ Failed to Send Message",
        description: `Sorry, there was an error. Please email us directly at info@dsljm.com or call 876-799-2106.`,
        variant: "destructive",
        duration: 8000,
      });
      console.error('Contact error:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/dsl-logo.png" alt="DSL Jamaica Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{siteConfig.companyName}</h1>
                <a href={`https://${siteConfig.domain}`} className="text-xs text-green-600 hover:text-green-700 font-semibold">
                  {siteConfig.domain}
                </a>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-green-600 transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-green-600 transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-green-600 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-green-600 transition-colors font-medium">Contact</button>
              <div className="flex items-center space-x-3 text-sm">
                <Phone className="h-4 w-4 text-green-600" />
                <a href={`tel:${siteConfig.contact.phone}`} className="text-gray-700 hover:text-green-600 font-medium">
                  {siteConfig.contact.phone}
                </a>
              </div>
              <Button onClick={() => scrollToSection('booking')} className="bg-green-600 hover:bg-green-700 text-white">Book Now</Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-20 pb-16 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Sparkling Clean Spaces,
                <span className="text-green-600"> Every Time</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Professional cleaning services for your home and business in Jamaica. Eco-friendly solutions that deliver exceptional results.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => scrollToSection('booking')} size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                  Schedule Service
                </Button>
                <Button onClick={() => scrollToSection('services')} size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                  View Services
                </Button>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">500+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">10+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">100%</div>
                  <div className="text-sm text-gray-600">Satisfaction</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1763865778196-e3f2e3dbd425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjbGVhbmluZyUyMHdvcmtlcnN8ZW58MHx8fHwxNzY2MjkzOTYwfDA&ixlib=rb-4.1.0&q=85"
                alt="Professional Cleaning Workers Team"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <Award className="h-10 w-10 text-green-600" />
                  <div>
                    <div className="font-bold text-gray-900">Certified & Insured</div>
                    <div className="text-sm text-gray-600">Licensed Professionals</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="py-8 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <span className="text-gray-700 font-medium">Eco-Friendly Products</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <span className="text-gray-700 font-medium">Licensed & Insured</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <span className="text-gray-700 font-medium">24/7 Availability</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <span className="text-gray-700 font-medium">Satisfaction Guaranteed</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to meet your specific needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-shadow duration-300 overflow-hidden border-0">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="bg-white/90 backdrop-blur-sm p-3 rounded-full">
                        <Icon className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button onClick={() => scrollToSection('booking')} variant="outline" className="w-full border-green-600 text-green-600 hover:bg-green-50">
                      Book This Service
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">About DSL Jamaica</h2>
              <p className="text-lg text-gray-600 mb-6">
                With over a decade of experience, DSL Jamaica has become the trusted name in professional cleaning services across the island. Our commitment to excellence and environmental responsibility sets us apart.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We pride ourselves on using eco-friendly cleaning products and sustainable practices while delivering exceptional results. Our trained team of professionals treats every space with care and attention to detail.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Users className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Expert Team</div>
                    <p className="text-sm text-gray-600">Trained and certified cleaning professionals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Leaf className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Eco-Friendly</div>
                    <p className="text-sm text-gray-600">Green cleaning products and practices</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Flexible Schedule</div>
                    <p className="text-sm text-gray-600">Available when you need us</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Quality Assured</div>
                    <p className="text-sm text-gray-600">100% satisfaction guarantee</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1869329/pexels-photo-1869329.jpeg"
                alt="Jamaica Caribbean Setting"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Don't just take our word for it</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardDescription className="text-base text-gray-700 italic">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-3 rounded-full">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Schedule Your Service</h2>
            <p className="text-xl text-gray-600">Book your cleaning appointment today</p>
          </div>
          <Card className="shadow-xl border-0">
            <CardContent className="pt-6">
              <form onSubmit={handleBookingSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="booking-name">Full Name *</Label>
                    <Input
                      id="booking-name"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-email">Email *</Label>
                    <Input
                      id="booking-email"
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="booking-phone">Phone Number *</Label>
                    <Input
                      id="booking-phone"
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      required
                      placeholder="876-XXX-XXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-service">Service Type *</Label>
                    <select
                      id="booking-service"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={bookingForm.service}
                      onChange={(e) => setBookingForm({...bookingForm, service: e.target.value})}
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.title}>{service.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Preferred Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {bookingForm.date ? format(bookingForm.date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={bookingForm.date}
                        onSelect={(date) => setBookingForm({...bookingForm, date})}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking-message">Additional Information</Label>
                  <Textarea
                    id="booking-message"
                    value={bookingForm.message}
                    onChange={(e) => setBookingForm({...bookingForm, message: e.target.value})}
                    placeholder="Tell us about your cleaning needs..."
                    rows={4}
                  />
                </div>
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" size="lg">
                  Submit Booking Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-8">
                Have questions? We're here to help. Reach out to us through any of the following methods.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Phone</div>
                    <a href={`tel:${siteConfig.contact.phone}`} className="text-gray-600 hover:text-green-600 transition-colors">
                      {siteConfig.contact.phone}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">Available 8:30 AM - 6:00 PM</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <MessageSquare className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">WhatsApp</div>
                    <a href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-600 transition-colors">
                      +{siteConfig.contact.whatsapp}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">Chat with us instantly</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Email</div>
                    <a href={`mailto:${siteConfig.contact.email}`} className="text-gray-600 hover:text-green-600 transition-colors">
                      {siteConfig.contact.email}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">Response within 24 hours</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Location</div>
                    <p className="text-gray-600">{siteConfig.contact.address}</p>
                    <div className="text-sm text-gray-500 mt-1">{siteConfig.contact.addressLine2}</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Card className="shadow-xl border-0">
                <CardHeader>
                  <CardTitle>Send Us a Message</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you soon</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Name *</Label>
                      <Input
                        id="contact-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        required
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        required
                        placeholder="How can we help you?"
                        rows={5}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" size="lg">
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/dsl-logo.png" alt="DSL Jamaica Logo" className="h-10 w-auto brightness-0 invert" />
                <div>
                  <h3 className="text-xl font-bold">{siteConfig.companyName}</h3>
                  <p className="text-xs text-green-400">{siteConfig.tagline}</p>
                </div>
              </div>
              <p className="text-gray-400 mb-3">
                Your trusted partner for all cleaning needs in Jamaica. Quality service, every time.
              </p>
              <a href={`https://${siteConfig.domain}`} className="text-green-400 hover:text-green-300 transition-colors">
                {siteConfig.domain}
              </a>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors">About</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">Commercial Cleaning</li>
                <li className="text-gray-400">Domestic Cleaning</li>
                <li className="text-gray-400">Waste Management</li>
                <li className="text-gray-400">Deep Cleaning</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">{siteConfig.contact.address}</li>
                <li><a href={`tel:${siteConfig.contact.phone}`} className="text-gray-400 hover:text-white transition-colors">{siteConfig.contact.phone}</a></li>
                <li><a href={`mailto:${siteConfig.contact.email}`} className="text-gray-400 hover:text-white transition-colors">{siteConfig.contact.email}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} {siteConfig.companyName}. All rights reserved. | Eco-friendly cleaning solutions for a better tomorrow.
            </p>
          </div>
        </div>
      </footer>

      {/* Booking Success Dialog */}
      <AlertDialog open={showBookingSuccess} onOpenChange={setShowBookingSuccess}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <AlertDialogTitle className="text-center text-2xl">
              Request Submitted Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base space-y-3">
              <p className="text-gray-700 font-medium">
                Thank you, {submittedData.name}!
              </p>
              <p className="text-gray-600">
                We've received your booking request for <span className="font-semibold text-green-600">{submittedData.service}</span>.
              </p>
              <p className="text-gray-600">
                Our team will contact you within 24 hours to confirm your appointment.
              </p>
              <div className="bg-green-50 p-4 rounded-lg mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Need immediate assistance?</strong><br />
                  Call us at: <a href="tel:876-799-2106" className="text-green-600 font-semibold">876-799-2106</a><br />
                  WhatsApp: <a href="https://wa.me/8767992106" className="text-green-600 font-semibold">+8767992106</a>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="w-full bg-green-600 hover:bg-green-700 text-white">
              Got it, Thanks!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contact Success Dialog */}
      <AlertDialog open={showContactSuccess} onOpenChange={setShowContactSuccess}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <AlertDialogTitle className="text-center text-2xl">
              Message Sent Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base space-y-3">
              <p className="text-gray-700 font-medium">
                Thank you for contacting us, {submittedData.name}!
              </p>
              <p className="text-gray-600">
                We've received your message and will respond within 24 hours.
              </p>
              <div className="bg-green-50 p-4 rounded-lg mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Need urgent assistance?</strong><br />
                  Call us at: <a href="tel:876-799-2106" className="text-green-600 font-semibold">876-799-2106</a><br />
                  Email: <a href="mailto:info@dsljm.com" className="text-green-600 font-semibold">info@dsljm.com</a>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="w-full bg-green-600 hover:bg-green-700 text-white">
              Got it, Thanks!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Home;
